import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { FdpDetailsSideNavigationComponent } from '@projects/adir/src/app/pages/fdp-details/fdp-details-side-navigation/fdp-details-side-navigation.component';
import { BreadcrumbComponent } from '@shared/src/lib/components/breadcrumb/breadcrumb.component';
import { BreadcrumbItem } from '@shared/src/lib/components/breadcrumb/breadcrumb.interface';
import { CardComponent } from '@shared/src/lib/components/card/card.component';
import { StatusChipComponent } from '@shared/src/lib/components/status-chip/status-chip.component';
import { StepperItem } from '@shared/src/lib/components/stepper/stepper-item/stepper-item.interface';
import { StepperComponent } from '@shared/src/lib/components/stepper/stepper.component';
import { InformationIconComponent } from '@shared/src/lib/icons/information-icon/information-icon.component';
import { CommonModule } from '@angular/common';
import { StatusChip } from '@shared/src/lib/components/status-chip/status-chip.interface';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { FdpDetailsComponent } from '@shared/src/lib/components/fdp-details/fdp-details.component';
import { Status } from '@shared/src/models/status';
import { getMpmWorkflowSteps, getNonMpmWorkflowSteps } from './fdp-details-layout.constant';
import { FdpService } from '@shared/src/services/fdp/fdp.service';
import { LocalStorageService } from '@shared/src/services/local-storage.service';
import { isMpmRole } from '@shared/src/lib/utils/role-util';
import { Fdp } from '../../../models/fdp.interface';
import { FdpTransferDataService } from '@shared/src/services/fdp/fdp-transfer-data.service';
import { Subscription } from 'rxjs';
import { FdpSubmissionStoreService } from '@shared/src/services/fdp/fdp-submission-store.service';

@Component({
  selector: 'app-fdp-details-layout',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    CardComponent,
    BreadcrumbComponent,
    StatusChipComponent,
    InformationIconComponent,
    StepperComponent,
    FdpDetailsSideNavigationComponent,
    ToastModule,
    FdpDetailsComponent,
  ],
  templateUrl: './fdp-details-layout.component.html',
  providers: [MessageService, FdpTransferDataService],
})
export class FdpDetailsLayoutComponent implements OnInit, OnDestroy {
  @ViewChild(FdpDetailsSideNavigationComponent)
  private readonly fdpDetailsSideNavigation: FdpDetailsSideNavigationComponent;

  requestId: number;
  selectedFdpSubmmitProject: string;
  fdpProjectType: string;
  currentWorkflow: Fdp.FdpDetail.FdpWorkflow;
  items: BreadcrumbItem[] = [
    { label: 'FDP Summary', url: '/fdp' },
    { label: '', url: '/' },
  ];
  requestStatus: Status;

  workflowHistory: Fdp.WorkflowHistory.Request.ResponseData[] = [];
  currentStep: StepperItem.WorkflowOption | null = null;

  public isMpmUser = false;
  public workflowSteps: StepperItem.WorkflowHistory[];

  private userObj: any = undefined;
  private saveSubscription: Subscription;

  constructor(
    private readonly localStorageService: LocalStorageService,
    private readonly fileStoreService: FdpSubmissionStoreService,
    private readonly fdpTranferDataService: FdpTransferDataService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly fdpService: FdpService
  ) {}

  get fdpSubmissionRequestStatus(): string {
    return this.currentWorkflow?.status ?? StatusChip.FdpStatus.DRAFT;
  }

  get chipStatusType(): string {
    return StatusChip.StatusType.FDP;
  }

  ngOnInit(): void {
    this.initControlsBasedOnRole();
    this.route.params.subscribe((params) => {
      if (!params['requestId']) {
        this.router.navigateByUrl('/fdp');
      }
      this.requestId = Number(params['requestId']);

      this.loadAndInitializeData();
    });

    this.items[1].url = this.router.url;

    this.saveSubscription = this.fileStoreService.fdpSubmissionSaveEvent.subscribe(() => {
      this.initControlsBasedOnRole();
      this.loadAndInitializeData();
    });
  }

  private loadAndInitializeData(): void {
    this.loadFdpRequestDetails();
    this.loadWorkflowAndStatus();
    this.fdpDetailsSideNavigation?.setupSideMenu(this.currentWorkflow);
  }

  private loadFdpRequestDetails(): void {
    this.fdpService.getFdpRequest(this.requestId).subscribe({
      next: (resp) => {
        this.selectedFdpSubmmitProject = resp.project.projectName;
        this.items[1].label = this.selectedFdpSubmmitProject;

        this.fdpProjectType = resp.project.projectType ?? '-';
        this.currentWorkflow = resp.currentWorkflow;
        this.fdpService.setCurrentWorkflow(this.currentWorkflow);
        this.fdpDetailsSideNavigation?.setupSideMenu(this.currentWorkflow);
        this.fdpTranferDataService.setRequestDetail(resp);
      },
      error: () => {
        this.router.navigateByUrl('/fdp');
        console.log('Error loading FDP submission detail');
      },
    });
  }

  private loadWorkflowAndStatus(): void {
    const defaultWorkflowSteps = getMpmWorkflowSteps();
    const [defaultStep] = defaultWorkflowSteps[0].workflows;
    this.currentStep = defaultStep;
    this.setSecondaryWorkflowStep(StepperItem.FDP_STEPPER_OPTIONS_LABEL.POSITION_PAPER_LETTER_SUBMISSION);

    this.fdpService.getWorkflowHistoryByRequestId({ requestId: this.requestId }).subscribe({
      next: (resp) => {
        this.workflowHistory = resp;
      },
      complete: () => {
        this.updateWorkflowProgress(this.workflowHistory);
      },
      error: () => {
        console.log('Error fetching workflow history');
      },
    });
  }

  private updateWorkflowProgress(workflowHistorySteps: StepperItem.WorkflowHistory[]): void {
    if (!this.currentStep) {
      const [firstFeature] = workflowHistorySteps;
      const [firstWorkflow] = firstFeature.workflows;
      this.currentStep = firstWorkflow;
    }

    this.enrichStepMetadata(workflowHistorySteps);
    this.currentStep = this.determineCurrentStep(this.workflowSteps);

    // TODO - Need to find a way to load secondaryWorkflowName from database, etc later - will handle in another ticket & PR
    const secondaryWorkflowName = StepperItem.FDP_STEPPER_OPTIONS_LABEL.POSITION_PAPER_LETTER_SUBMISSION;
    this.setSecondaryWorkflowStep(secondaryWorkflowName);
  }

  private enrichStepMetadata(workflowHistorySteps: StepperItem.WorkflowHistory[]): void {
    if (workflowHistorySteps.length === 1) {
      const lastWorkflowStep = this.workflowSteps[0].workflows.find((wflow) => wflow.isLastElement);
      if (lastWorkflowStep) {
        const lastWorkflowIndex = this.workflowSteps[0].workflows.indexOf(lastWorkflowStep);
        const previousWorkflowStep =
          lastWorkflowIndex > 0 ? this.workflowSteps[0].workflows[lastWorkflowIndex - 1] : null;

        if (
          previousWorkflowStep &&
          this.currentWorkflow?.status === StatusChip.FdpStatus.REJECTED &&
          this.currentWorkflow?.workflow === StepperItem.FDP_STEPPER_OPTIONS_LABEL.PROGRESS_REVIEW
        ) {
          previousWorkflowStep.isLastElement = false;
          lastWorkflowStep.isHidden = false;
          lastWorkflowStep.isLastElement = true;
        }
      }

      this.workflowSteps = this.workflowSteps.map((workflowStep) => ({
        ...workflowStep,
        workflows: workflowStep.workflows.map((wflow) => {
          const wflowResult = workflowHistorySteps[0].workflows.find((wf) => wf.workflowName == wflow.workflowName);
          return {
            ...wflow,
            isCompleted: wflowResult?.isCompleted === true,
            isHidden: wflowResult?.isHidden === true || wflow.isHidden,
            requestStatus: wflowResult?.requestStatus,
            focal: wflowResult?.focal,
          };
        }),
      }));
    }
  }

  private determineCurrentStep(uiWorkflowSteps: StepperItem.WorkflowHistory[]): StepperItem.WorkflowOption | null {
    const latestCompletedIndex = this.findLatestCompletedStepIndex(uiWorkflowSteps);

    if (latestCompletedIndex === null) {
      return uiWorkflowSteps[0].workflows[0];
    }

    const nextIncompleteStep = this.findNextIncompleteStep(uiWorkflowSteps);
    if (nextIncompleteStep) {
      return nextIncompleteStep.workflows.find((workflow) => !workflow.isCompleted)!;
    }

    const completedWorkflow = uiWorkflowSteps[latestCompletedIndex]?.workflows.find((workflow) => workflow.isCompleted);

    return completedWorkflow ?? null;
  }

  private setSecondaryWorkflowStep(secondaryWorkflowName: string): void {
    if (this.currentStep && this.isMpmUser) {
      this.currentStep = this.addSecondaryWorkflowStep(this.currentStep, secondaryWorkflowName);
    }
  }

  private addSecondaryWorkflowStep(
    workflowStep: StepperItem.WorkflowOption,
    secondaryWorkflowName: string
  ): StepperItem.WorkflowOption | null {
    return { ...workflowStep, secondaryWorkflowName: secondaryWorkflowName };
  }

  private findLatestCompletedStepIndex(uiWorkflowSteps: StepperItem.WorkflowHistory[]): number | null {
    let lastCompletedIndex: number | null = null;

    for (const featureHistory of uiWorkflowSteps) {
      const completedIndexes = featureHistory.workflows
        .map((workflow, index) => ({
          isCompleted: workflow.isCompleted,
          index,
        }))
        .filter((item) => item.isCompleted)
        .map((item) => item.index);

      if (completedIndexes.length > 0) {
        completedIndexes.sort((a, b) => a - b);

        const latestCompletedInFeature = completedIndexes.pop();

        if (latestCompletedInFeature !== undefined) {
          if (lastCompletedIndex === null || latestCompletedInFeature > lastCompletedIndex) {
            lastCompletedIndex = latestCompletedInFeature;
          }
        }
      }
    }

    return lastCompletedIndex;
  }

  private findNextIncompleteStep(steps: StepperItem.WorkflowHistory[]): StepperItem.WorkflowHistory | null {
    return (
      steps.find((step) => {
        return step.workflows.find((workflow) => {
          return workflow.isCompleted === false;
        });
      }) ?? null
    );
  }

  private initControlsBasedOnRole(): void {
    const userInfo = this.localStorageService.getData('user');
    if (userInfo) {
      this.userObj = JSON.parse(userInfo) ?? {};
      this.isMpmUser = isMpmRole(this.userObj.roleName);

      if (this.isMpmUser) {
        this.workflowSteps = getMpmWorkflowSteps();
      } else {
        this.workflowSteps = getNonMpmWorkflowSteps();
      }
    }
  }

  ngOnDestroy(): void {
    if (this.saveSubscription) {
      this.saveSubscription.unsubscribe();
    }
  }
}
